import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import type { DateRange } from "@capacit-com/lara-law-shared-ui";

import { cn } from "@/lib/utils";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Calendar } from "@capacit-com/lara-law-shared-ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { FC } from "react";
import { DateTime } from "luxon";

type FiltersProps = {
  titlePrefix?: string;
  className?: string;
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
  presetText: string | undefined;
  setPresetText: (presetText: string | undefined) => void;
};

const DATE_OPTIONS = [
  { label: "I dag", days: 0 },
  { label: "I går", days: 1 },
  { label: "Sidste 7 dage", days: 7 },
  { label: "Sidste 30 dage", days: 30 },
  { label: "Sidste 180 dage", days: 180 },
  { label: "Sidste 365 dage", days: 365 },
] as const;

const Filters: FC<FiltersProps> = ({
  titlePrefix,
  className,
  date,
  setDate,
  presetText,
  setPresetText,
}) => {
  const onDateChange = (date: DateRange) => {
    if (date.to) {
      const selectedTo = DateTime.fromJSDate(date.to);
      date.to = selectedTo.endOf("day").toJSDate();
    }
    if (date.from) {
      const selectedFrom = DateTime.fromJSDate(date.from);
      date.from = selectedFrom.startOf("day").toJSDate();
    }
    setDate(date);
  };

  return (
    <div className={cn("flex", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "min-w-[200px] gap-2 font-normal bg-card shadow-sm",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="shrink-0" />
            <div className="flex-1 text-center">
              {date?.from ? (
                date.to ? (
                  <>
                    {titlePrefix && (
                      <span className="text-muted-foreground">
                        {titlePrefix + "  "}
                      </span>
                    )}
                    {presetText ? (
                      presetText
                    ) : (
                      <>
                        {format(date.from, "LLL dd, y")} -{" "}
                        {format(date.to, "LLL dd, y")}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {titlePrefix && (
                      <span className="text-muted-foreground pr-2">
                        {titlePrefix}
                      </span>
                    )}
                    {presetText ? (
                      presetText
                    ) : (
                      <>{format(date.from, "LLL dd, y")}</>
                    )}
                  </>
                )
              ) : (
                <span>Vælg en dato</span>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex flex-row justify-between h-full">
            <div className="flex flex-col gap-2 p-2">
              {DATE_OPTIONS.map((option) => (
                <Button
                  key={option.label}
                  variant={"ghost"}
                  className="flex justify-start"
                  onClick={() => {
                    const from = DateTime.now()
                      .startOf("day")
                      .minus({ days: option.days })
                      .toJSDate();
                    const to =
                      option.label === "I går"
                        ? DateTime.now()
                            .endOf("day")
                            .minus({ days: 1 })
                            .toJSDate()
                        : DateTime.now().endOf("day").toJSDate();
                    onDateChange({ from, to });
                    setPresetText(option.label);
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            <div className="h-auto my-4 w-px bg-border" />
            <Calendar
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={(range) => {
                if (range) {
                  onDateChange(range);
                  setPresetText(undefined);
                }
              }}
              numberOfMonths={2}
              buttonVariant={"outline"}
              classNames={{
                root: "w-[500px]",
              }}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default Filters;
